import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { StyledEngineProvider } from "@mui/material/styles";
import { Box,Tooltip, Typography, IconButton, Grid, Checkbox } from "@mui/material"; 
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next"; 
import { useParams } from "react-router-dom";
import themeAr from "../../themes/index.ar";
import themeEn from "../../themes/index.en";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { GeneralStyles } from "../../assets/css/general.styles";
import PersonalInformation from "./personalInformation";
import { RegisterTouched } from "./registerTouched";
import { RegisterProps } from "./registerProps";
import {
  emailRegex,
  kuwaitPhoneNumberDigits,
  kuwaitPhoneNumberRegex,
  Lowercase,
  Numbercase,
  passwordCharactersMinimum,
  Sections,
  SpecialChar,
  Uppercase,
} from "../../constants/registerConstants";
import IncomeDetails from "./incomeDetails";
import InvestmentPreference from "./investmentPreference";
import BankDetails from "./bankDetails";
import PoliticalPosition from "./politicalPosition";
import FATCA from "./fatca";
import ReportableJurisdiction from "./reportableJurisdiction";
import { getCountries } from "../../reducers/countryReducer";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { RootState } from "../../store/store";
import CustomButton from "../../components/customButton/customButton";
import GridStyles from "../../assets/css/grid.styles";
import { InvestorModel } from "../../models/models/investorModel";
import {
  getInvestorById,
  registerInvestor,
  saveInvestor,
} from "../../reducers/investorReducer"; 
import { Countries } from "../../constants/enums";
import { PARENT_DOMAIN } from "../../constants/domain";
import Loader from "../../components/loader";
import { sendActionStatus } from "../../reducers/externalApiReducer";
import { RegistrationProps } from "./registrationProps";  

const componentsMap: {
  [key: number]: React.FC<RegisterProps> & {
    validate?: (
      formData: InvestorModel,
      confirmPassword?: string,
      ibanIdentifier?: string
    ) => RegisterTouched;
  };
} = {
  1: PersonalInformation,
  2: IncomeDetails,
  3: InvestmentPreference,
  4: BankDetails,
  5: PoliticalPosition,
  6: FATCA,
  7: ReportableJurisdiction,
};

const initialFormData: InvestorModel = {
  id: 0,
  email: "",
  mobileNumber: null,
  mobileCountryId: Countries.KuwaitId,
  password: "",
  firstName: "",
  middleName: "",
  lastName: "",
  aicPortfolioNumber: "",
  civilIdNumber: null,
  passportNumber: "",
  civilIdExpiryDate: "",
  aicClientId: null,
  birthDate: "",
  birthPlace: "",
  genderId: null,
  civilIdDocument: null,
  countryId: null,
  cityId: null,
  city: "",
  area: "",
  suburb: "",
  suburbId: null,
  areaId: null,
  block: "",
  street: "",
  buildingName: "",
  buildingNumber: "",
  postalCode: "",
  passportDocument: null,
  receiveCommunicationId: null,
  nationalities: [],
  employers: [
    {
      id: 0,
      name: "",
      address: "",
      phoneNumber: null,
      phoneCountryId: Countries.KuwaitId,
      designationId: null,
      email: "",
    },
  ],
  incomeDetails: {
    id: 0,
    hasSalaryIncome: false,
    hasFinancingIncome: false,
    hasPrivateBusinessIncome: false,
    hasOtherIncomeSource: false,
    otherIncomeSource: "",
    monthlyIncomeId: null,
    averageAnnualIncomeId: null,
    estimatedWealthId: null,
  },
  investmentPreference: {
    id: 0,
    hasPreviousSecurities: false,
    hasPreviousSukok: false,
    hasPreviousBonds: false,
    hasPreviousRealState: false,
    hasPreviousInvestmentFunds: false,
    hasOtherPreviousExperience: false,
    otherPreviousExperience: "",
    isQualifiedProfessionalInvestor: false,
    isProfessionalInvestor: false,
    isRetailInvestor: false,
    investmentObjectiveId: null,
    otherInvestmentObjective: "",
    riskToleranceId: null,
    preferredProductsId: null,
    otherPreferredProducts: "",
    hasHighLiquidityRiskUnderstand: null,
    hasInvestmentLossUnderstand: null,
    hasUncertainPayoutsUnderstand: null,
    hasWithdrawalRightsUnderstand: null,
    canInvestProfessionalLimit: null,
    canInvestRetailLimit: null,
    canInvestRetailOfferingLimit: null,
    hasLimitsWithdrawalRightsUnderstand: null,
    hasRiskComprehensionConfirmation: null,
    hasDocumentReviewedConfirmation: null,
    hasContentsUnderstoodConfirmation: null,
    hasProfessionalLimitConfirmation: null,
    hasRetailLimitConfirmation: null,
    hasRetailOfferingLimitConfirmation: null,
    isInfoAcknowledgmentConfirmed: null,
  },
  bankDetails: {
    id: 0,
    isOwnAccountInvesting: false,
    isBehalfOthersInvesting: false,
    actualBeneficiaryFullName: "",
    isKuwaitiBankAccount: null,
    kuwaitiBankId: null,
    bankName: "",
    bankBranchName: "",
    accountNumber: "",
    iban: "",
    other: "",
    beneficiaries: [
      {
        id: 0,
        name: "",
        relationshipId: null,
      },
    ],
  },
  politicalPosition: {
    id: 0,
    pepPositionId: null,
    familyMembers: [
      {
        id: 0,
        pepPositionId: null,
        name: "",
        relationshipId: null,
        civilIdNumber: null,
      },
    ],
  },
  fatca: {
    id: 0,
    hasUsaRelation: null,
    bornPlace: "",
    passportHolder: "",
    socialSecurityNumber: "",
    greenCardNumber: "",
    bankAccountNumber: "",
    residenceAddress: "",
    holdMailAddress: "",
    internationalMobileCountryId: Countries.KuwaitId,
    internationalMobileNumber: null,
    outsideKuwaitTaxIdentificationNumber: "",
    usaTaxIdentificationNumber: "",
    personalEmail: "",
    designationId: null,
    employerName: "",
    employerAddress: "",
    employerContactDetails: "",
    hasInternalRevenueServiceAgreement: null,
    hasTransferFundsInstruction: null,
    hasAttorneyPower: null,
  },
  reportableJurisdiction: {
    id: 0,
    isReportableJurisdictionResident: null,
    reportableJurisdiction: "",
    hasResidenceAddress: null,
    residenceAddress: "",
    hasTelephoneNumbers: null,
    telephoneNumbers: "",
    hasHoldMailInstruction: null,
    holdMailInstruction: "",
    hasInstructionTransferFunds: null,
    instructionTransferFunds: "",
    hasPowerAttorney: null,
    powerAttorney: "",
  },
};

const Registration: React.FC<RegistrationProps> = ({ lang }) => {  
  const { t }: { t: (key: string) => string } = useTranslation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const isRTL = currentLanguage === 'ar'; 

  useEffect(() => {
    i18n.changeLanguage(lang); 
  }, [lang]);

  const { id } = useParams();
  const navigate = useNavigate();
  const [showGoToTop, setShowGoToTop] = useState(false);
  const [showGoToBottom, setShowGoToBottom] = useState(true);
  const dispatch = useAppDispatch();
  const [activeSection, setActiveSection] = useState<number | null>(
    Sections[0]?.id || null
  );
  const [formSubmitted, setFormSubmitted] = useState<boolean | null>(null);
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [registerConfirmation, setRegisterConfirmation] = useState<boolean>(false);
  const authToken = useSelector((state: RootState) => state.auth.token);
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [ibanIdentifier, setIbanIdentifier] = useState("");
  const countries = useSelector((state: RootState) => state.country.countries);
  const [formData, setFormData] = useState<InvestorModel>(initialFormData);
  const [touched, setTouched] = useState<RegisterTouched>({});
  const investorDetails = useSelector(
    (state: RootState) => state.investor.investorDetails
  );

  const [hovered, setHovered] = useState(false); 

  useEffect(() => {
    dispatch(getCountries());
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (id) {
      const itemId = parseInt(id, 10);
      dispatch(getInvestorById({ id: itemId }));
    }
  }, [id]);

  useEffect(() => {
    if (id && !authToken) {
      navigate("/Investor/Signin");
    }
  }, [authToken, id]);

  useEffect(() => {
    if (id) {
      const itemData = investorDetails?.data;

      if (itemData) {
        const data = itemData as InvestorModel;

        setConfirmPassword(data.password || "");
        setFormData((prevFormData) => ({
          id: data.id || 0,
          email: data.email || "",
          mobileNumber: data.mobileNumber || null,
          mobileCountryId: data.mobileCountryId || null,
          password: data.password || "",
          firstName: data.firstName || "",
          middleName: data.middleName || "",
          lastName: data.lastName || "",
          aicPortfolioNumber: data.aicPortfolioNumber || "",
          civilIdNumber: data.civilIdNumber || null,
          passportNumber: data.passportNumber || "",
          civilIdExpiryDate: data.civilIdExpiryDate || "",
          aicClientId: data.aicClientId || null,
          birthDate: data.birthDate || "",
          birthPlace: data.birthPlace || "",
          genderId: data.genderId || null,
          civilIdDocument: data.civilIdDocument || null,
          countryId: data.countryId || null,
          cityId: data.cityId || null,
          city: data.city || "",
          area: data.area || "",
          suburb: data.suburb || "",
          suburbId: data.suburbId || null,
          areaId: data.areaId || null,
          block: data.block || "",
          street: data.street || "",
          buildingName: data.buildingName || "",
          buildingNumber: data.buildingNumber || "",
          postalCode: data.postalCode || "",
          passportDocument: data.passportDocument || null,
          receiveCommunicationId: data.receiveCommunicationId || null,
          nationalities: data.nationalities || [],
          employers:
            data.employers.length !== 0
              ? data.employers?.map((employer) => ({
                  id: employer.id || 0,
                  name: employer.name || "",
                  address: employer.address || "",
                  phoneNumber: employer.phoneNumber || null,
                  phoneCountryId: employer.phoneCountryId || Countries.KuwaitId,
                  designationId: employer.designationId || null,
                  email: employer.email || "",
                }))
              : prevFormData.employers,
          incomeDetails: {
            id: data.incomeDetails.id || 0,
            hasSalaryIncome: data.incomeDetails.hasSalaryIncome || false,
            hasFinancingIncome: data.incomeDetails.hasFinancingIncome || false,
            hasPrivateBusinessIncome:
              data.incomeDetails.hasPrivateBusinessIncome || false,
            hasOtherIncomeSource:
              data.incomeDetails.hasOtherIncomeSource || false,
            otherIncomeSource: data.incomeDetails.otherIncomeSource || "",
            monthlyIncomeId: data.incomeDetails.monthlyIncomeId || null,
            averageAnnualIncomeId:
              data.incomeDetails.averageAnnualIncomeId || null,
            estimatedWealthId: data.incomeDetails.estimatedWealthId || null,
          },
          investmentPreference: {
            id: data.investmentPreference.id || 0,
            hasPreviousSecurities:
              data.investmentPreference.hasPreviousSecurities || false,
            hasPreviousSukok:
              data.investmentPreference.hasPreviousSukok || false,
            hasPreviousBonds:
              data.investmentPreference.hasPreviousBonds || false,
            hasPreviousRealState:
              data.investmentPreference.hasPreviousRealState || false,
            hasPreviousInvestmentFunds:
              data.investmentPreference.hasPreviousInvestmentFunds || false,
            hasOtherPreviousExperience:
              data.investmentPreference.hasOtherPreviousExperience || false,
            otherPreviousExperience:
              data.investmentPreference.otherPreviousExperience || "",
            isQualifiedProfessionalInvestor:
              data.investmentPreference.isQualifiedProfessionalInvestor ||
              false,
            isProfessionalInvestor:
              data.investmentPreference.isProfessionalInvestor || false,
            isRetailInvestor:
              data.investmentPreference.isRetailInvestor || false,
            investmentObjectiveId:
              data.investmentPreference.investmentObjectiveId || null,
            otherInvestmentObjective:
              data.investmentPreference.otherInvestmentObjective || "",
            riskToleranceId: data.investmentPreference.riskToleranceId || null,
            preferredProductsId:
              data.investmentPreference.preferredProductsId || null,
            otherPreferredProducts:
              data.investmentPreference.otherPreferredProducts || "",
            hasHighLiquidityRiskUnderstand:
              data.investmentPreference.hasHighLiquidityRiskUnderstand || false,
            hasInvestmentLossUnderstand:
              data.investmentPreference.hasInvestmentLossUnderstand || false,
            hasUncertainPayoutsUnderstand:
              data.investmentPreference.hasUncertainPayoutsUnderstand || false,
            hasWithdrawalRightsUnderstand:
              data.investmentPreference.hasWithdrawalRightsUnderstand || false,
            canInvestProfessionalLimit:
              data.investmentPreference.canInvestProfessionalLimit || false,
            canInvestRetailLimit:
              data.investmentPreference.canInvestRetailLimit || false,
            canInvestRetailOfferingLimit:
              data.investmentPreference.canInvestRetailOfferingLimit || false,
            hasLimitsWithdrawalRightsUnderstand:
              data.investmentPreference.hasLimitsWithdrawalRightsUnderstand ||
              false,
            hasRiskComprehensionConfirmation:
              data.investmentPreference.hasRiskComprehensionConfirmation ||
              false,
            hasDocumentReviewedConfirmation:
              data.investmentPreference.hasDocumentReviewedConfirmation ||
              false,
            hasContentsUnderstoodConfirmation:
              data.investmentPreference.hasContentsUnderstoodConfirmation ||
              false,
            hasProfessionalLimitConfirmation:
              data.investmentPreference.hasProfessionalLimitConfirmation ||
              false,
            hasRetailLimitConfirmation:
              data.investmentPreference.hasRetailLimitConfirmation || false,
            hasRetailOfferingLimitConfirmation:
              data.investmentPreference.hasRetailOfferingLimitConfirmation ||
              false,
            isInfoAcknowledgmentConfirmed:
              data.investmentPreference.isInfoAcknowledgmentConfirmed || false,
          },
          bankDetails: {
            id: data.bankDetails.id || 0,
            isOwnAccountInvesting:
              data.bankDetails.isOwnAccountInvesting || false,
            isBehalfOthersInvesting:
              data.bankDetails.isBehalfOthersInvesting || false,
            actualBeneficiaryFullName:
              data.bankDetails.actualBeneficiaryFullName || "",
            isKuwaitiBankAccount:
              data.bankDetails.isKuwaitiBankAccount || false,
            kuwaitiBankId: data.bankDetails.kuwaitiBankId || null,
            bankName: data.bankDetails.bankName || "",
            bankBranchName: data.bankDetails.bankBranchName || "",
            accountNumber: data.bankDetails.accountNumber || "",
            iban: data.bankDetails.iban || "",
            other: data.bankDetails.other || "",
            beneficiaries:
              data.bankDetails.beneficiaries.length !== 0
                ? data.bankDetails?.beneficiaries?.map((beneficiary) => ({
                    id: beneficiary.id || 0,
                    name: beneficiary.name || "",
                    relationshipId: beneficiary.relationshipId || null,
                  }))
                : prevFormData.bankDetails.beneficiaries,
          },
          politicalPosition: {
            id: data.politicalPosition.id || 0,
            pepPositionId: data.politicalPosition.pepPositionId || null,
            familyMembers:
              data.politicalPosition.familyMembers.length !== 0
                ? data.politicalPosition?.familyMembers?.map(
                    (familyMember) => ({
                      id: familyMember.id || 0,
                      name: familyMember.name || "",
                      relationshipId: familyMember.relationshipId || null,
                      pepPositionId: familyMember.pepPositionId || null,
                      civilIdNumber: familyMember.civilIdNumber || null,
                    })
                  )
                : prevFormData.politicalPosition.familyMembers,
          },
          fatca: {
            id: data.fatca.id || 0,
            hasUsaRelation: data.fatca.hasUsaRelation || false,
            bornPlace: data.fatca.bornPlace || "",
            passportHolder: data.fatca.passportHolder || "",
            socialSecurityNumber: data.fatca.socialSecurityNumber || "",
            greenCardNumber: data.fatca.greenCardNumber || "",
            bankAccountNumber: data.fatca.bankAccountNumber || "",
            residenceAddress: data.fatca.residenceAddress || "",
            holdMailAddress: data.fatca.holdMailAddress || "",
            internationalMobileCountryId:
              data.fatca.internationalMobileCountryId || Countries.KuwaitId,
            internationalMobileNumber:
              data.fatca.internationalMobileNumber || null,
            outsideKuwaitTaxIdentificationNumber:
              data.fatca.outsideKuwaitTaxIdentificationNumber || "",
            usaTaxIdentificationNumber:
              data.fatca.usaTaxIdentificationNumber || "",
            personalEmail: data.fatca.personalEmail || "",
            designationId: data.fatca.designationId || null,
            employerName: data.fatca.employerName || "",
            employerAddress: data.fatca.employerAddress || "",
            employerContactDetails: data.fatca.employerContactDetails || "",
            hasInternalRevenueServiceAgreement:
              data.fatca.hasInternalRevenueServiceAgreement || false,
            hasTransferFundsInstruction:
              data.fatca.hasTransferFundsInstruction || false,
            hasAttorneyPower: data.fatca.hasAttorneyPower || false,
          },
          reportableJurisdiction: {
            id: data.reportableJurisdiction.id || 0,
            isReportableJurisdictionResident:
              data.reportableJurisdiction.isReportableJurisdictionResident ||
              false,
            reportableJurisdiction:
              data.reportableJurisdiction.reportableJurisdiction || "",
            hasResidenceAddress:
              data.reportableJurisdiction.hasResidenceAddress || false,
            residenceAddress:
              data.reportableJurisdiction.residenceAddress || "",
            hasTelephoneNumbers:
              data.reportableJurisdiction.hasTelephoneNumbers || false,
            telephoneNumbers:
              data.reportableJurisdiction.telephoneNumbers || "",
            hasHoldMailInstruction:
              data.reportableJurisdiction.hasHoldMailInstruction || false,
            holdMailInstruction:
              data.reportableJurisdiction.holdMailInstruction || "",
            hasInstructionTransferFunds:
              data.reportableJurisdiction.hasInstructionTransferFunds || false,
            instructionTransferFunds:
              data.reportableJurisdiction.instructionTransferFunds || "",
            hasPowerAttorney:
              data.reportableJurisdiction.hasPowerAttorney || false,
            powerAttorney: data.reportableJurisdiction.powerAttorney || "",
          },
        }));
      }
    }
  }, [id, investorDetails.data]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      setShowGoToTop(scrollTop > 100);
      setShowGoToBottom(scrollTop + clientHeight < scrollHeight - 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
 
  useEffect(() => {
    const sendHeight = () => {
      const mainDiv = document.getElementById("mainDiv");
      if (!!mainDiv) {
        const height = mainDiv.scrollHeight + 10;
        window.parent.postMessage({ height }, PARENT_DOMAIN);
      }
    };
    sendHeight();
  }, [
    activeSection,
    touched,
    hovered,
    formData.employers.length,
    formData.investmentPreference.isQualifiedProfessionalInvestor,
    formData.investmentPreference.isProfessionalInvestor,
    formData.investmentPreference.isRetailInvestor,
    formData.bankDetails.isOwnAccountInvesting,
    formData.bankDetails.isBehalfOthersInvesting,
    formData.bankDetails.beneficiaries.length,
    formData.politicalPosition.familyMembers.length,
  ]);
 

  const validateEmployers = () => {
    const updatedTouchedEmployers = formData.employers.map((employer) => ({
      name: employer.name.trim() === "",
      address: employer.address.trim() === "",
      phoneNumber:
        employer.phoneNumber === null ||
        !kuwaitPhoneNumberRegex.test(employer.phoneNumber?.toString()) ||
        employer.phoneNumber?.toString().length !== kuwaitPhoneNumberDigits,
      phoneCountryId: employer.phoneCountryId === null,
      email: !emailRegex.test(employer.email),
      designationId: employer.designationId === null,
    }));

    setTouched((prevTouched) => ({
      ...prevTouched,
      employers: updatedTouchedEmployers,
    }));
  };
  const validateFamilyMembers = () => {
    const hasFamilyMemberErrors = formData.politicalPosition.familyMembers.map(
      (family) => ({
        name: family.name.trim() === "",
        civilIdNumber: family.civilIdNumber === null,
        pepPositionId: family.pepPositionId === null,
        relationshipId: family.relationshipId === null,
      })
    );

    setTouched((prevTouched) => ({
      ...prevTouched,
      familyMembers: hasFamilyMemberErrors,
    }));
  };

  const validateBeneficiaries = () => {
    const hasErrors = formData.bankDetails.beneficiaries.map((item) => ({
      name: item.name.trim() === "",
      relationshipId: item.relationshipId === null,
    }));
    setTouched((prevTouched) => ({
      ...prevTouched,
      beneficiaries: hasErrors,
    }));
  };

  const handleRegister = () => {
    if(!registerConfirmation){
      toast.warning(`${t('confirmRegistration')}`);
      return;
    }
    let allValid = true;
    const allErrors: RegisterTouched = {};

    Sections.forEach((section) => {
      const Component = componentsMap[section.id];
      if (Component?.validate) {
        const errors = Component.validate(
          formData,
          confirmPassword,
          ibanIdentifier
        );
        Object.assign(allErrors, errors);
        Object.entries(errors).forEach(([field, value]) => {
          if (value) {
            allValid = false;
          }
        });
      }
    });
    setTouched(allErrors);

    if (allValid) {
      setFormSubmitted(false);
      dispatch(registerInvestor({ data: formData }))
        .unwrap()
        .then((response) => {
          const { token, messageCode, success } = response;
          if (success) {
            //toast.success(`${t('saveInvestor')}`);
            dispatch(
              sendActionStatus({
                token,
                status: true,
                action: "",
                message: `${t('saveInvestor')}`,
              })
            );
          } else {
            dispatch(
              sendActionStatus({
                token: "",
                status: false,
                action: "",
                message: `${t(messageCode)}`,
              })
            );
          }
        })
        .catch((error) => {
          console.error("Failed to save item:", error);
        });
    } else {
      setActiveSection(0);
      validateEmployers();
      validateBeneficiaries();
      validateFamilyMembers();
      setFormSubmitted(true);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      dispatch(sendActionStatus({token:'', status:false, action:'',message:`${t('mandatoryForm')}` })); 
    }
  };

  const validateForm = () => {
    const newTouched = {
      email: formData.email.trim() === "" || !emailRegex.test(formData.email),
      mobileNumber:
        (formData.mobileNumber !== null &&
          (!kuwaitPhoneNumberRegex.test(formData.mobileNumber.toString()) ||
            formData.mobileNumber.toString().length !=
              kuwaitPhoneNumberDigits)) ||
        formData.mobileNumber === null,
      receiveCommunicationId: !formData.receiveCommunicationId,
      password: formData.id === 0 && 
       ( formData.password.trim() === "" ||
       formData.password.length < passwordCharactersMinimum ||
       !Uppercase.test(formData.password) ||
       !Lowercase.test(formData.password) ||
       !Numbercase.test(formData.password) ||
       !SpecialChar.test(formData.password) ||
       (formData.firstName.toLowerCase().trim() !== "" &&
         formData.password
           .toLowerCase()
           .includes(formData.firstName?.toLowerCase())) ||
       (formData.lastName.toLowerCase().trim() !== "" &&
         formData.password
           .toLowerCase()
           .includes(formData.lastName?.toLowerCase()))),
      confirmPassword:formData.id === 0 &&  
        (confirmPassword.trim() === "" || confirmPassword !== formData.password),
    };

    setTouched(newTouched);

    const isValid = !Object.values(newTouched).some((value) => value === true);

    return isValid;
  };

  const handleSaveAndContinue = () => {
    if (!validateForm()) {
      setFormSubmitted(true);
      setActiveSection(1);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      }); 
      dispatch(sendActionStatus({token:'', status:false, action:'',message:`${t('mandatoryForm')}` }));  
      return;
    }
    setFormSubmitted(false);
    dispatch(saveInvestor({ data: formData }))
      .unwrap()
      .then((response) => {
        const { token, messageCode, success } = response;
        if (success) {
          dispatch(
            sendActionStatus({
              token,
              status: true,
              action: "",
              message: `${t('saveInvestor')}`,
            })
          );
        } else {
          dispatch(
            sendActionStatus({
              token: "",
              status: false,
              action: "",
              message: `${t(messageCode)}`,
            })
          );
        }
      })
      .catch((error) => {
        console.error("Failed to save item:", error);
      });
  };

  return (
    <ThemeProvider theme={isRTL ? themeAr : themeEn}>
    <StyledEngineProvider injectFirst> 
        <Box sx={{textAlign: isRTL ? "right" : "left" }}>
          {investorDetails.isLoading && <Loader />}
          <Box>
            <Typography
              variant="h5"
              fontWeight="bold"
              p={2}
              sx={{ textAlign: isRTL ? "right" : "left" }}
            >
              {t("createInvestorAccount")}
            </Typography>
            <Typography p={2} sx={{ textAlign: isRTL ? "right" : "left" }}>
              {t("summarize")}
            </Typography>

            <Box display="flex" flexDirection="column" mt={4}>
              {Sections.map((section, index) => {
                const Component = componentsMap[section.id];
                return (
                  <Box key={section.id} position="relative">
                    {index < Sections.length - 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "40px", 
                          [isRTL ? "right" : "left"]: "25px",
                          height: "calc(100% + 16px)",
                          width: "1px",
                          backgroundImage: `radial-gradient(${
                            activeSection === section.id
                              ? themeEn.palette.primary.dark
                              : themeEn.palette.secondary.dark
                          } 75%, transparent 75%)`,
                          backgroundPosition: "0 0",
                          backgroundSize: "8px 6px",
                          backgroundRepeat: "repeat-y",
                          zIndex: 0,
                        }}
                      />
                    )}
                    <Box
                      display="flex"
                      alignItems="center" 
                      my={1}
                      onClick={() =>
                        setActiveSection(
                          activeSection === section.id ? null : section.id
                        )
                      }
                      sx={{
                        cursor: "pointer",
                        textAlign: isRTL ? "right" : "left",
                        color:
                          activeSection === section.id
                            ? themeEn.palette.primary.dark
                            : themeEn.palette.secondary.dark, 
                        display: "flex", 
                        alignItems: "center",
                        justifyContent: isRTL ? "flex-end" : "flex-start", 
                        marginBottom: "20px",
                      }}
                    >
                      {isRTL && (
                        <Typography variant="body1" sx={{ marginBottom: "20px", marginRight:"10px" }}>
                            {t(`${section.title}`)}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          textAlign: "center",
                          width: 30,
                          height: 30,
                          marginBottom: "20px",
                          borderRadius: "50%",
                          border: "2px solid",
                          borderColor:
                            Component.validate &&
                            formSubmitted !== null &&
                            formSubmitted &&
                            Object.values(
                              Component.validate(
                                formData,
                                confirmPassword,
                                ibanIdentifier
                              ) || {}
                            ).some((value) => value)
                              ? themeEn.palette.error.main
                              : activeSection === section.id
                              ? themeEn.palette.primary.dark
                              : themeEn.palette.secondary.dark,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          marginLeft: isRTL ? 0 : "8px",
                          marginRight: isRTL ? "8px" : 0,
                          backgroundColor:
                            Component.validate &&
                            formSubmitted !== null &&
                            formSubmitted &&
                            Object.values(
                              Component.validate(
                                formData,
                                confirmPassword,
                                ibanIdentifier
                              ) || {}
                            ).some((value) => value)
                              ? themeEn.palette.error.main
                              : activeSection === section.id
                              ? themeEn.palette.primary.dark
                              : themeEn.palette.text.secondary,
                          color:
                            Component.validate &&
                            formSubmitted !== null &&
                            formSubmitted &&
                            Object.values(
                              Component.validate(
                                formData,
                                confirmPassword,
                                ibanIdentifier
                              ) || {}
                            ).some((value) => value)
                              ? themeEn.palette.text.secondary
                              : activeSection === section.id
                              ? themeEn.palette.text.secondary
                              : themeEn.palette.secondary.dark,
                        }}
                      >
                        {section.id}
                      </Box>
                      {!isRTL && (
                      <Typography variant="body1" sx={{ marginBottom: "20px",  marginLeft:"10px" }}>
                          {t(`${section.title}`)}
                      </Typography>
                      )} 
                    </Box>

                    {activeSection === section.id && Component && (
                      <>
                        <Component
                          formData={formData}
                          setFormData={setFormData}
                          touched={touched}
                          t={t}
                          isRTL={isRTL}
                          setTouched={setTouched}
                          confirmPassword={confirmPassword}
                          setConfirmPassword={setConfirmPassword}
                          ibanIdentifier={ibanIdentifier}
                          setIbanIdentifier={setIbanIdentifier}
                          countries={
                            Array.isArray(countries.data) ? countries.data : []
                          }
                          handleSaveAndContinue={handleSaveAndContinue}
                          validateEmployers={validateEmployers}
                          validateFamilyMembers={validateFamilyMembers}
                          validateBeneficiaries={validateBeneficiaries}
                        />
                      <Box
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center" 
                          sx={{ 
                            position: "sticky",
                            bottom: 10,
                            left: !isRTL ? '100%' : 0,
                            right: isRTL ? '100%' : 0,
                            width: "fit-content",
                            zIndex: 1000, 
                          }}
                          onMouseEnter={() => setHovered(true)}
                          onMouseLeave={() => setHovered(false)}
                        >
                          {hovered ? (
                          <CustomButton
                            type="button"
                            style={{
                              ...GridStyles.continueLaterButton,
                              minWidth: "150px", 
                            }}
                            onClick={handleSaveAndContinue}
                          >
                            {t("saveAndContinue")}
                          </CustomButton>
                          ):(

                            <Tooltip title={t("saveAndContinue")} placement="left">
                              <IconButton
                                onClick={handleSaveAndContinue}
                                sx={{
                                  backgroundColor: "primary.main",
                                  color: "white",
                                  "&:hover": { backgroundColor: "primary.dark" },
                                }}
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box> 
                    </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

            {/* {showAllButtons ? (  */}
              <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              border={`1px solid ${themeEn.palette.text.disabled}`}
              sx={{
                p: 2,
                borderRadius: 1,
                mt: 4,
                backgroundColor: themeEn.palette.background.default,
                boxShadow: "0 -1px 5px rgba(0, 0, 0, 0.1)",
                textAlign: isRTL ? "right" : "left",
              }}
            >
                <Grid container item xs={12} sm={12} md={12} spacing={2}>
                
                    {isRTL ?
                    (
                      <>
                      <Grid item xs={12} sm={6} md={2}>
                        <CustomButton
                        type="button"
                        style={GridStyles.registerButton}
                        onClick={handleRegister}
                      >
                        {t("register")}
                      </CustomButton>
                      </Grid>   
                      <Grid item xs={12} sm={6} md={2}>
                      <CustomButton
                        type="button"
                        style={GridStyles.continueLaterButton}
                        onClick={handleSaveAndContinue}
                      >
                        {t("saveAndContinue")}
                      </CustomButton>
                      </Grid>
                      <Grid container item xs={12} sm={12} md={8}>
                        <Grid item xs={10} sm={10} md={11}>
                        <Typography variant="body1" sx={{ wordWrap: "break-word" }}>
                          {t("confirmDetails")}
                        </Typography>
                        <Typography variant="body1" sx={{ wordWrap: "break-word" }}>
                          {t("falseInfoLegalLiability")}
                        </Typography>
                        <Typography
                          variant="body1"
                          mt={1}
                          sx={{ wordWrap: "break-word" }}
                        >
                          {t("notifyChangesInWriting")}
                        </Typography>
                        </Grid> 
                        <Grid item xs={2} sm={2} md={1}>
                        <Checkbox 
                          size="small" 
                          style={GeneralStyles.checkBoxStyle} 
                          onChange={(e: any) => { 
                            const { name, checked } = e.target;
                            setRegisterConfirmation(checked);
                          }}
                        />
                        </Grid>
                      </Grid>
                    </>
                    ) : (
                      <>
                      <Grid container item xs={12} sm={12} md={8}>
                        <Grid item xs={2} sm={2} md={1}>
                        <Checkbox 
                          size="small" 
                          style={GeneralStyles.checkBoxStyle} 
                          onChange={(e: any) => { 
                            const { name, checked } = e.target;
                            setRegisterConfirmation(checked);
                          }}
                        />
                        </Grid>
                        <Grid item xs={10} sm={10} md={11}>
                        <Typography variant="body1" sx={{ wordWrap: "break-word" }}>
                          {t("confirmDetails")}
                        </Typography>
                        <Typography variant="body1" sx={{ wordWrap: "break-word" }}>
                          {t("falseInfoLegalLiability")}
                        </Typography>
                        <Typography
                          variant="body1"
                          mt={1}
                          sx={{ wordWrap: "break-word" }}
                        >
                          {t("notifyChangesInWriting")}
                        </Typography>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                      <CustomButton
                        type="button"
                        style={GridStyles.continueLaterButton}
                        onClick={handleSaveAndContinue}
                      >
                        {t("saveAndContinue")}
                      </CustomButton>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <CustomButton
                        type="button"
                        style={GridStyles.registerButton}
                        onClick={handleRegister}
                      >
                        {t("register")}
                      </CustomButton>
                      </Grid>  
                    </>
                    )}  
                </Grid> 
              </Box>
            {/* ):(  
              <Box  
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              border={`1px solid ${themeEn.palette.text.disabled}`}
              sx={{
                p: 2,
                position: "sticky",
                bottom: 0,
                zIndex: 1000,
                borderRadius: 1,
                // mt: 4,
                backgroundColor: themeEn.palette.background.default,
                boxShadow: "0 -1px 5px rgba(0, 0, 0, 0.1)",
                textAlign: isRTL ? "right" : "left",
              }}
            >
                <Grid container item xs={12} sm={12} md={12} spacing={2}> 
                    {isRTL ?
                    (
                      <>  
                      <Grid item xs={12} sm={6} md={2}>
                      <CustomButton
                        type="button"
                        style={GridStyles.continueLaterButton}
                        onClick={handleSaveAndContinue}
                      >
                        {t("saveAndContinue")}
                      </CustomButton>
                      </Grid> 
                    </>
                    ) : (
                      <>  
                      <Grid item xs={12} sm={6} md={10}></Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <CustomButton
                          type="button"
                          style={GridStyles.continueLaterButton}
                          onClick={handleSaveAndContinue}
                        >
                          {t("saveAndContinue")}
                        </CustomButton>
                      </Grid>  
                    </>
                    )}  
                </Grid> 
              </Box>
            )} */}
        </Box> 
    </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Registration;
